import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {CreativeCardScratchpadState} from '../../scratchpad/creativeCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {Creative, CreativeNodeData} from 'common/src/modules/creatives/creatives.types';
import getCreativeForCampaign from '../../utils/creative/getCreativeForCampaign';
import validateCreativeToBeUsedAsFallbackCreative from '../../utils/creative/validateCreativeToBeUsedAsFallbackCreative';
import describeIdFormat from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/describeIdFormat';
import {idHelper} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.config';
import {IdPrefix} from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/idHelpers.enums';
import formatParseError from 'common/src/modules/campaigns/copilot/skills/campaignSetupSkill/idHelpers/utils/formatParseError';

const setCreativeInputSchema = z.object({
  creativeId: z.string().describe(describeIdFormat(IdPrefix.Creative)),
});

function toCreativeNodeData(creative: Creative): CreativeNodeData {
  return {
    id: creative.id,
    name: creative.name,
    poster: creative.poster,
    productType: creative.productType.value,
    type: creative.type.value,
    features: creative.features,
    staticCreativeData: creative.staticCreativeData,
    template: creative.template,
    formats: creative.formats.map((format) => ({
      width: format.width,
      height: format.height,
      minItemCount: format.minItemCount,
      maxItemCount: format.maxItemCount,
      videoProp: format.videoProp,
      issues: format.issues,
      verificationResult: format.verificationResult,
      rendererVersion: format.RendererVersion,
      isAvailable: true,
    })),
    issues: creative.issues,
    tag1Value: creative.tag1Value,
    tag2Value: creative.tag2Value,
    tag3Value: creative.tag3Value,
  };
}

function setCreativeTool(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof setCreativeInputSchema, CreativeCardScratchpadState>,
  ) {
    const {creativeId} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<CreativeCardScratchpadState>(
      agentScratchpad,
      NodeType.CREATIVE,
    );

    const parseResult = idHelper.parseCreativeId(creativeId);

    if (parseResult.ok === false) {
      return toolError(formatParseError(parseResult.error));
    }

    const numericId = parseResult.numericId;
    const cardId = validatedScratchpad.card.id;
    const isFallback = validatedScratchpad.card.isFallbackCreative ?? false;

    const result = await getCreativeForCampaign(store, numericId, cardId, isFallback);

    if (result.found === false) {
      return toolError(result.errorMessage);
    }

    const {creative} = result;

    if (isFallback) {
      const fallbackValidation = validateCreativeToBeUsedAsFallbackCreative(creative);

      if (fallbackValidation.isValid === false) {
        return toolError(fallbackValidation.errorMessage);
      }
    }

    validatedScratchpad.card.creativeId = numericId;
    validatedScratchpad.card.creative = toCreativeNodeData(creative);

    return toolResult(
      `Creative "${creative.name}" (${idHelper.toCreativeId(numericId)}) set successfully.`,
    );
  }

  return tool<typeof setCreativeInputSchema, CreativeCardScratchpadState>({
    name: V2CardAgentToolName.SetCreative,
    displayName: 'Setting creative',
    description:
      'Set the creative on the card. Validates the creative exists and is compatible with the campaign. For fallback creatives, validates the creative does not use products, location, content, or weather.',
    inputSchema: setCreativeInputSchema,
    execute,
  });
}

export default setCreativeTool;
